package nl.volleyplanner.web.controller.planner.rulesets_config_v2

import kotlinx.coroutines.Deferred
import kotlinx.coroutines.asPromise
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.html.ButtonType
import kotlinx.html.FlowContent
import kotlinx.html.TagConsumer
import kotlinx.html.button
import kotlinx.html.classes
import kotlinx.html.div
import kotlinx.html.h5
import kotlinx.html.h6
import kotlinx.html.js.onClickFunction
import kotlinx.html.style
import nl.volleyplanner.api.planner.Ruleset
import nl.volleyplanner.web.CoroutineManager.RENDER_SCOPE
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component.OffcanvasComponent
import nl.volleyplanner.web.base.component.OffcanvasPosition
import nl.volleyplanner.web.base.component.badge
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.util.BootstrapColor
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import kotlin.js.Promise

class RulesetSelectorOffcanvas(
    private val dataManager: TeamPouleConfigDataManager
) : OffcanvasComponent(
    "vp-ruleset-selector",
    offcanvasPosition = OffcanvasPosition.End,
    allowBodyScroll = false,
    showBackdrop = true
) {
    private val selectedRulesetIdFlow = MutableStateFlow<String?>(null)
    private var latestOnSelected: (Ruleset?) -> Unit = {}
    private var templateEditorComponent: Deferred<RulesetTemplateEditorComponent>? = null

    override fun initialize() {
        super.initialize()
        templateEditorComponent = RENDER_SCOPE.async { createStandaloneComponent(RulesetTemplateEditorComponent(dataManager)) }
    }

    override fun onContentReplaced(htmlElement: HTMLElement) {
        super.onContentReplaced(htmlElement)
        templateEditorComponent = RENDER_SCOPE.async { createStandaloneComponent(RulesetTemplateEditorComponent(dataManager)) }
    }

    override fun FlowContent.renderHeader() {
        h5 { +"Selecteer template" }
        button(classes = "btn btn-close") {
            attributes["data-bs-dismiss"] = "offcanvas"
            attributes["aria-label"] = "Close"
        }
    }

    fun showSelector(selectedRulesetId: String?, onSelected: (Ruleset?) -> Unit) {
        selectedRulesetIdFlow.value = selectedRulesetId
        latestOnSelected = onSelected
        show()
    }

    override fun FlowContent.renderBody() {
        button(classes = "btn btn-sm btn-primary mb-2") {
            onClickFunction = { event ->
                event.preventDefault()
                RENDER_SCOPE.async {
                    templateEditorComponent!!.await().show()
                }
            }

            icon("bi-pencil-square")
            +"Templates bewerken"
        }

        h6 { +"Klik op een template om deze te selecteren" }
        val dummyRuleset = Ruleset(
            identifier = "dummy",
            name = "Geen template",
            description = "",
            priority = -10,
            archived = false,
            refereeRequiredAmount = 0,
            scoreKeeperRequiredAmount = 0,
            primaryRefereeGroups = emptyList(),
            alternativeRefereeGroups = emptyList(),
            primaryScoreKeeperGroups = emptyList(),
            alternativeScoreKeeperGroups = emptyList()
        )
        (dataManager.availableRulesets.value + dummyRuleset)
            .sortedBy { it.priority }
            .forEach { ruleset ->
            button (classes = "card vp-btn-card w-100 text-start shadow-sm p-2 mb-2 rounded") {
                type = ButtonType.button
                onClickFunction = { event ->
                    event.preventDefault()
                    latestOnSelected(ruleset.takeIf { it.identifier != "dummy" })
                    hide()
                }
                if (ruleset.identifier == selectedRulesetIdFlow.value) {
                    classes += "vp-selected"
                }

                div (classes = "d-flex flex-wrap mb-2") {
                    h6(classes = "mb-0") { +ruleset.name }

                    if (ruleset.identifier == selectedRulesetIdFlow.value) {
                        div (classes = "ms-auto d-flex align-items-center") {
                            badge(BootstrapColor.Success, small = true) { +"Huidig" }
                        }
                    } else {
                        div (classes = "ms-auto vp-btn-card-on-hover align-items-center") {
                            style = "--vp-btn-card-on-hover-display: flex;"
                            badge(BootstrapColor.Primary, small = true) { +"Selecteer" }
                        }
                    }
                }

                rulesetCardInfo(ruleset.takeIf { it.identifier != "dummy" })
            }
        }
    }

    override suspend fun getState() = dataManager.availableRulesets
        .combineAndLaunch(RENDER_SCOPE, selectedRulesetIdFlow)
}

class RulesetSelectorButtonComponent(
    private val dataManager: TeamPouleConfigDataManager,
    private val rulesetSelectorOffcanvas: RulesetSelectorOffcanvas,
    private val selectedRulesetId: String?,
    private val onSelected: (Ruleset?) -> Unit = {}
) : HTMLComponent() {
    private val selectedRulesetIdFlow = MutableStateFlow<String?>(selectedRulesetId)

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val selectedRuleset = dataManager.availableRulesets.value.find { it.identifier == selectedRulesetIdFlow.value }
        return consumer.div(classes = "btn-group") {
            button(classes = "btn btm-sm dropdown-toggle vp-btn-tpc-ruleset-selector") {
                if (dataManager.selectedSeasonFlow.value?.active == false) {
                    classes += setOf("disabled", "btn-secondary")
                } else {
                    if (selectedRuleset != null) {
                        classes += "btn-primary"
                    } else {
                        classes += "btn-secondary"
                    }
                }
                style = "--bs-btn-font-size: .75rem; --bs-btn-padding-y: .15rem; --bs-btn-padding-x: .4rem;"
                type = ButtonType.button
                onClickFunction = { event ->
                    event.preventDefault()
                    rulesetSelectorOffcanvas.showSelector(selectedRulesetIdFlow.value, onSelected = { ruleset ->
                        selectedRulesetIdFlow.value = ruleset?.identifier
                        onSelected(ruleset)
                    })
                }
                +(selectedRuleset?.name ?: "Geen selectie")
            }
        }
    }

    override suspend fun getState() = dataManager.availableRulesets
        .combineAndLaunch(RENDER_SCOPE, selectedRulesetIdFlow)
}